import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import role from "../../redux/actions/role";
import { makeStyles } from "@mui/styles";
import { theme } from "../../../../yts_reports/src/styles/theme";
import MathJaxContent from "../../components/MathJaxContent";
import MonacoEditor from "@monaco-editor/react";
import CloseIcon from "@mui/icons-material/Close";

const colorMapping = {
  primary: "#1976d2",
  warning: "#FF941C",
  error: "#F44336",
  success: "#4CAF50",
  customColor1: "#FF5733",
  customColor2: "#33FF57",
};

// Mapping of status to color and text
const statusMapping = {
  correct: {
    color: "#4CAF50",
    text: "Answered Correctly",
    chipText: "Correct",
  },
  incorrect: {
    color: "#F44336",
    text: "Answered Incorrectly",
    chipText: "Incorrect",
  },
  skip: {
    color: "#707070",
    text: "Question Skipped",
    chipText: "Skipped",
  },
  partiallyCorrect: {
    color: "#FF941C",
    text: "Partially Correct",
    chipText: "Partially Correct",
  },
};

const difficultyColorMapping = {
  Beginner: "#4CAF50",
  Intermediate: "#FF941C",
  Advanced: "#F44336",
};

const useStyles = makeStyles({
  eventsText: {
    fontSize: "10px !important",
    fontStyle: "normal !important",
    lineHeight: "24px !important",
  },
  eventsTitle: {
    fontSize: "10px !important",
    fontStyle: "normal !important",
    overflow: "hidden !important",
    textOverflow: "ellipsis !important",
    whiteSpace: "normal !important",
    wordWrap: "break-word !important",
  },
  progess: {
    color: theme.palette.btnPrimary.tertiaryText,
    [theme.breakpoints.up("tablet")]: {},
  },
  ques_st: theme.typography.b1Medium,
});

const TimelineSlider = ({
  // currentTime,
  onTimeChange,
  videoDuration,
  events,
  sessionData,
  hidePrimary,
  compact,
  selectedEvents,
  setSelectedEvents,
  scaleFactor,
  type,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const roleState = useSelector((state) => state.role);
  const [eventsData, setEventsData] = useState(events);
  const [open, setOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [question, setQuestion] = useState("");
  const [transcription, setTranscription] = useState("");
  const qadata = roleState?.invitedetails?.qaData;
  const [openSeeQuestion, setOpenSeeQuestion] = useState(false);
  const [questionDescription, setQuestionDescription] = useState("");
  const [questionOptions, setQuestionOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [answerStatus, setAnswerStatus] = useState("");
  const [selectedEventState, setSelectedEventState] = useState(null);
  const [openSeeQuestionCoding, setOpenSeeQuestionCoding] = useState(false);
  const [source, setSource] = useState("");
  const [testCases, setTestCases] = useState([]);

  const CustomSlider = styled(Slider)(({ theme }) => ({
    color: "#1976d2", // color of line
    height: 0,
    "& .MuiSlider-thumb": {
      // height: 12,
      // width: 12,
      display: "none",
    },

    "& .MuiSlider-rail": {
      // height: 0,
      opacity: 1.0,
    },
    "& .MuiSlider-markActive": {
      opacity: 1, // Ensure active marks keep the same opacity
    },
    "& .MuiSlider-markLabel": {
      top: "-12px !important", // Adjust this value to position the labels above the slider
      color: "#000",
      fontSize: "12px",
    },
  }));

  const AnswerOptionContainer = styled("div")(
    ({ theme, isSelected, isCorrect, isUserCorrect, showCorrect, isMultiSelect }) => ({
      width: "100%",
      borderRadius: 4,
      backgroundColor: isSelected ? (isUserCorrect ? "#d4edda" : isMultiSelect ? "#61A2E4" : "#f8d7da") : "#EBEFF8",
      border: isCorrect || showCorrect ? "2px solid #28a745" : "1px solid",
      borderColor: isSelected
        ? isUserCorrect
          ? "#c3e6cb"
          : isMultiSelect
          ? "transparent"
          : "#f5c6cb"
        : isCorrect || showCorrect
        ? "#28a745"
        : "transparent",
      fontSize: 13,
      padding: "14px 12px",
      whiteSpace: "pre-wrap",
      marginTop: theme.spacing(2),
      color: isMultiSelect && isSelected ? "#fff" : "inherit",
    })
  );

  const maxTime = useMemo(
    () => Math.max(...eventsData?.map((event) => (compact ? event.startTime : event.pseudoStartTime))),
    [eventsData, compact]
  );

  const handleSliderChange = (event, newValue) => {
    // Find the nearest event that is within 5 seconds of the clicked time
    const nearestEvent = eventsData.reduce((nearest, currentEvent) => {
      const currentDiff = Math.abs(newValue - currentEvent.pseudoStartTime);
      const nearestDiff = nearest ? Math.abs(newValue - nearest.pseudoStartTime) : Infinity;

      if (currentDiff < nearestDiff && currentDiff <= 5) {
        return currentEvent;
      }
      return nearest;
    }, null);

    if (nearestEvent) {
      // Set the time to the startTime of the nearest event
      onTimeChange(nearestEvent.startTime);

      // Toggle the selection of the nearest event in `selectedEvents`
      setSelectedEvents((prevSelected) => {
        const updatedSelected = [...prevSelected];
        const eventIndex = updatedSelected.findIndex((e) => e.pseudoStartTime === nearestEvent.pseudoStartTime);

        if (eventIndex > -1) {
          // If the event is already selected, remove it
          updatedSelected.splice(eventIndex, 1);
        } else {
          // If the event is not selected, add it
          updatedSelected.push(nearestEvent);
        }

        return updatedSelected;
      });
    } else {
      // set video with curresponding time
      const proportionalTime = (newValue / maxTime) * videoDuration;
      onTimeChange(compact ? newValue : proportionalTime);
    }
  };

  // console.log(eventsData, "eventsData");

  const timeInMinutes = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Generate marks dynamically, only showing the label for the selected event
  const marks = useMemo(() => {
    // Filter and map the events
    const filteredEvents = eventsData
      ?.filter((event) => !hidePrimary || event.color !== "primary")
      .map((event) => ({
        value: compact ? event.startTime : event.pseudoStartTime,
        label: selectedEvents.some((e) => e.startTime === event.startTime) ? timeInMinutes(event.startTime) : "", // Show label only for selected event
        color: event.color,
      }));

    // Conditionally add the start and end points if hidePrimary is true
    if (hidePrimary) {
      const startPoint = {
        value: 0,
        label: selectedEvents.some((e) => e.startTime === 0) ? timeInMinutes(0) : "", // Show label only if selected
        color: "primary",
      };

      const endPoint = {
        value: maxTime,
        label: selectedEvents.some((e) => e.startTime === maxTime) ? timeInMinutes(maxTime) : "", // Show label only if selected
        color: "primary",
      };

      return [startPoint, ...filteredEvents, endPoint];
    }

    return filteredEvents;
  }, [eventsData, hidePrimary, selectedEvents, maxTime, compact]);

  // console.log(marks, "marks");

  const getEventColor = (colorName) => colorMapping[colorName] || "#1976d2";

  const handleMarkAsNonIssue = async (eventId) => {
    // console.log(eventId, "eventId");
    // console.log(sessionData, "sessionData");
    const payload = {
      key: eventId.key,
      status: "success",
      code: sessionData.code,
    };
    dispatch(role.updateTimelineEvents(payload));

    // Remove the clicked event from selectedEvents
    setSelectedEvents((prevSelected) => prevSelected.filter((e) => e.startTime !== eventId.startTime));
  };

  const handleShowQuestion = (selectedEvents) => {
    const questionID = selectedEvents.questionID;
    const questionData = qadata[questionID];
    setQuestion(questionData?.question || "Question not found");
    const transcriptionAns = questionData?.transcription?.replace(/\\n/g, "<br>");
    setTranscription(transcriptionAns || "Transcription not found");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowTranscript(false);
  };

  useEffect(() => {
    if (roleState?.updateTimelineEventsSuccess?.timeline) {
      setEventsData(roleState?.updateTimelineEventsSuccess?.timeline);
      dispatch(role.clearUpdateTimelineEvents());
    }
    // eslint-disable-next-line
  }, [roleState?.updateTimelineEventsSuccess]);

  // Calculate the minimum time difference between consecutive events
  // const minTimeDifference = useMemo(() => {
  //   return eventsData.reduce((minDiff, event, index, array) => {
  //     if (index === 0) return minDiff;
  //     const diff = Math.abs(event.startTime - array[index - 1].startTime);
  //     if (diff > 0) {
  //       return diff < minDiff ? diff : minDiff;
  //     }
  //     return minDiff;
  //   }, Infinity);
  // }, [eventsData]);

  // Set the width of the timeline based on the maximum time value
  // const timelineWidth = useMemo(
  //   () => (!compact ? (maxTime / minTimeDifference) * 2 : null), // Adjust the multiplier as needed
  //   [maxTime, minTimeDifference, compact]
  // );

  // console.log(timelineWidth, "timelineWidth");

  // Check if the first or last event is selected
  const isFirstEventSelected = useMemo(
    () => selectedEvents.some((e) => e.startTime === eventsData[0]?.startTime),
    [selectedEvents, eventsData]
  );
  const isLastEventSelected = useMemo(
    () => selectedEvents.some((e) => e.startTime === eventsData[eventsData.length - 1]?.startTime),
    [selectedEvents, eventsData]
  );

  // Adjust padding based on the selection of the first or last event
  const paddingLeft = isFirstEventSelected ? "70px" : "30px";
  const paddingRight = isLastEventSelected ? "70px" : "30px";

  const handleSeeQuestion = (selectedEvent) => {
    setLoading(true);
    setQuestionDescription("");
    setQuestionOptions([]);
    const payload = {
      trackingId: selectedEvent.trackingId,
      code: sessionData.code,
    };
    dispatch(role.getQuesAnsByTrackingId(payload));
    setOpenSeeQuestion(true);
    setAnswerStatus(selectedEvent?.data?.status);
    setSelectedEventState(selectedEvent);
  };

  useEffect(() => {
    if (roleState?.getQuesAnsByTrackingIdSuccess) {
      setQuestionDescription(roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Question_Description);

      const options = ["Answer_A", "Answer_B", "Answer_C", "Answer_D", "Answer_E"]
        .map((key) => ({
          text: roleState?.getQuesAnsByTrackingIdSuccess?.qus?.[key],
          key: key.slice(-1), // Extract the last character (A, B, C, D, E)
        }))
        .filter((option) => option.text); // Filter out undefined options

      setQuestionOptions(options);
      setLoading(false);
    }
  }, [roleState?.getQuesAnsByTrackingIdSuccess]);

  // console.log(questionOptions, "opopo");

  // console.log(roleState?.getQuesAnsByTrackingIdSuccess, "roleState?.getQuesAnsByTrackingIdSuccess");

  const handleCloseSeeQuestion = () => {
    setOpenSeeQuestion(false);
    setQuestionDescription("");
    setQuestionOptions([]);
    setLoading(true);
    setOpenSeeQuestionCoding(false);
  };

  // look for the full skill name in skillDetails array
  const getFullSkillName = (shortName, skillDetails) => {
    const skill = skillDetails.find((s) => s.skill === shortName);
    return skill ? skill.skillFullName : shortName;
  };

  const handleSeeQuestionCoding = (selectedEvent) => {
    setOpenSeeQuestionCoding(true);
    setSelectedEventState(selectedEvent);
    const payload = {
      code: sessionData.code,
    };
    dispatch(role.codingSubmissionDetails(payload));
    setLoading(true);
  };

  useEffect(() => {
    if (roleState?.codingSubmissionDetailsSuccess) {
      const codingData = roleState?.codingSubmissionDetailsSuccess[selectedEventState?.questionID];
      if (codingData) {
        setQuestionDescription(codingData.qus);
        setSource(codingData.source);
        setTestCases(codingData.testCases);
        setLoading(false);
      }
    }
  }, [roleState?.codingSubmissionDetailsSuccess, selectedEventState]);

  const handlePreviousQuestion = () => {
    const currentIndex = Object.keys(roleState.codingSubmissionDetailsSuccess).indexOf(
      selectedEventState.questionID.toString()
    );
    if (currentIndex > 0) {
      const previousQuestionId = Object.keys(roleState.codingSubmissionDetailsSuccess)[currentIndex - 1];
      setSelectedEventState(eventsData.find((event) => event.questionID.toString() === previousQuestionId));
    }
  };

  const handleNextQuestion = () => {
    const currentIndex = Object.keys(roleState.codingSubmissionDetailsSuccess).indexOf(
      selectedEventState.questionID.toString()
    );
    if (currentIndex < Object.keys(roleState.codingSubmissionDetailsSuccess).length - 1) {
      const nextQuestionId = Object.keys(roleState.codingSubmissionDetailsSuccess)[currentIndex + 1];
      setSelectedEventState(eventsData.find((event) => event.questionID.toString() === nextQuestionId));
    }
  };

  const mapLanguage = (codingLang) => {
    switch (codingLang?.toLowerCase()) {
      case "c++":
        return "cpp";
      case "c#":
        return "csharp";
      case "golang":
        return "go";
      default:
        return codingLang?.toLowerCase();
    }
  };

  const issueEventTypes = ["noFace", "candidateMissing", "multiFace"];

  return (
    <>
      <Box
        sx={{
          // width: `${timelineWidth}%`,
          width: !compact ? `${100 * scaleFactor}%` : null,
          minWidth: "90%",
          margin: "auto",
          bgcolor: "#E0E0E0",
          padding: `40px ${paddingRight} 40px ${paddingLeft}`,
          overflowX: "auto",
          whiteSpace: "nowrap",
          position: "relative",
        }}
      >
        <CustomSlider
          value={maxTime}
          aria-labelledby="timeline-slider"
          valueLabelDisplay="off"
          step={1}
          marks={marks}
          min={0}
          max={maxTime}
          onChange={handleSliderChange}
          sx={{
            "& .MuiSlider-mark": {
              height: compact ? 8 : 10,
              width: compact ? 8 : 10,
              borderRadius: "50%",
              border: "5px solid #E0E0E0",
              translate: "-9px",
            },
            ...marks?.reduce((acc, event, idx) => {
              acc[`& .MuiSlider-mark[data-index="${idx}"]`] = {
                backgroundColor: getEventColor(event.color),
              };
              return acc;
            }, {}),
          }}
        />

        {/* Relative container for callouts */}
        <Box
          sx={{
            position: "relative",
            height: selectedEvents.length > 0 ? "50px" : 0, // Adjust height for callouts
            transition: "height 0.3s ease", // Smooth transition for height changes
          }}
        >
          {selectedEvents.map((selectedEvent, index) => {
            const isLastEvent = selectedEvent.startTime === eventsData[eventsData.length - 1]?.startTime;
            return (
              <Box
                key={index}
                sx={{
                  position: "absolute",
                  top: "-18px", // Adjust this value to position the callout below the event circle
                  left: `${(selectedEvent.pseudoStartTime / maxTime) * 100}%`,
                  transform: compact ? "translateX(-51.5%)" : "translateX(-50.5%)", //  value to center the callout
                  p: "4px 6px",
                  bgcolor: "#fff",
                  borderRadius: "4px",
                  maxWidth: "110px",
                  zIndex: 1,
                  border: index === selectedEvents.length - 1 ? "1px solid black" : "1px solid #757575", // Conditional border color
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderBottom: index === selectedEvents.length - 1 ? "10px solid black" : "10px solid #757575", // Conditional border color
                  }}
                />
                <Typography className={classes.eventsTitle}>{selectedEvent.title}</Typography>
                {!isLastEvent && selectedEvent.color === "primary" && (
                  <>
                    <Typography className={classes.eventsText}>
                      {selectedEvent.timeTaken < 60
                        ? Math.round(selectedEvent.timeTaken) + " sec"
                        : timeInMinutes(selectedEvent.timeTaken) + " min"}{" "}
                      {selectedEvent.eventType === "selectQ" ? "spent" : "on this Ques"}
                    </Typography>
                    {type !== "coding" && (
                      <Typography
                        sx={{ color: statusMapping[selectedEvent?.data?.status]?.color || "grey" }}
                        className={classes.eventsText}
                      >
                        <b>{statusMapping[selectedEvent?.data?.status]?.text || selectedEvent?.data?.status}</b>
                      </Typography>
                    )}
                    {selectedEvent.trackingId &&
                      type !== "coding" &&
                      roleState?.invitedetails?.viewQuestions === true && (
                        <Button
                          variant="text"
                          sx={{ fontSize: "10px", textTransform: "none", padding: 0, minWidth: "auto" }}
                          onClick={() => handleSeeQuestion(selectedEvent)}
                        >
                          See Question
                        </Button>
                      )}

                    {type === "coding" && roleState?.invitedetails?.viewQuestions === true && (
                      <Button
                        variant="text"
                        sx={{ fontSize: "10px", textTransform: "none", padding: 0, minWidth: "auto" }}
                        onClick={() => handleSeeQuestionCoding(selectedEvent)}
                      >
                        See Submission
                      </Button>
                    )}
                  </>
                )}
                {!isLastEvent && issueEventTypes.includes(selectedEvent.eventType) && (
                  <Typography className={classes.eventsText}>
                    for about{" "}
                    {selectedEvent.timeTaken < 60
                      ? Math.round(selectedEvent.timeTaken) + " sec"
                      : timeInMinutes(selectedEvent.timeTaken) + " min"}{" "}
                  </Typography>
                )}
                {(selectedEvent.eventType === "focusout" || issueEventTypes.includes(selectedEvent.eventType)) &&
                  selectedEvent.color !== "success" && (
                    <Button
                      variant="text"
                      sx={{ fontSize: "10px", textTransform: "none", padding: 0, minWidth: "auto" }}
                      onClick={() => handleMarkAsNonIssue(selectedEvent)}
                    >
                      Mark as non-issue
                    </Button>
                  )}

                {(selectedEvent.eventType === "focusout" || issueEventTypes.includes(selectedEvent.eventType)) &&
                  selectedEvent.color === "success" && (
                    <Typography sx={{ color: "#4CAF50" }} className={classes.eventsText}>
                      <b>Marked as non-issue</b>
                    </Typography>
                  )}
                {selectedEvent.eventType === "startQ" && (
                  <Button
                    variant="text"
                    sx={{ fontSize: "10px", textTransform: "none", padding: 0, minWidth: "auto" }}
                    onClick={() => handleShowQuestion(selectedEvent)}
                  >
                    Show question
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
        <DialogTitle>Question</DialogTitle>
        <DialogContent>
          <Typography>{question}</Typography>

          <Collapse in={showTranscript}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Transcript
            </Typography>
            <Typography sx={{ mt: 2 }} dangerouslySetInnerHTML={{ __html: transcription }} />
          </Collapse>
          <Button
            variant="text"
            sx={{ fontSize: "10px", textTransform: "none", padding: 0, minWidth: "auto", mt: 2 }}
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? "Hide Answer Transcript" : "See Answer Transcript"}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog fullWidth maxWidth="md" open={openSeeQuestion} onClose={handleCloseSeeQuestion}>
        <DialogTitle>
          {!loading && selectedEventState && `Question ${selectedEventState?.questionNo} `}
          {answerStatus && !loading && (
            <Chip
              label={statusMapping[answerStatus]?.chipText}
              sx={{
                backgroundColor: statusMapping[answerStatus]?.color,
                color: "#fff",
                right: "20px",
                position: "absolute",
              }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ padding: "50px 200px" }}>
          {loading ? (
            <>
              <Skeleton variant="text" width="20%" height={30} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mt: 2, borderRadius: 1 }} />
            </>
          ) : (
            <>
              <Grid display={"flex"} gap={"5px"}>
                {type === "technical" && roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill && (
                  <Typography color={"primary"} fontWeight={600}>
                    {getFullSkillName(
                      roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill,
                      roleState?.invitedetails?.jobRole?.skillDetails
                    )}
                  </Typography>
                )}
                {type === "presentation" && roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill && (
                  <Typography color={"primary"} fontWeight={600}>
                    {roleState?.invitedetails?.behavioralDimensions[
                      roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill
                    ] || roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill}
                  </Typography>
                )}
                {type === "technical" &&
                  roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Skill &&
                  roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Difficulty && (
                    <Typography fontWeight={600}>|</Typography>
                  )}
                {type === "technical" && roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Difficulty && (
                  <Typography
                    fontWeight={600}
                    sx={{
                      color: difficultyColorMapping[roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Difficulty],
                    }}
                  >
                    {roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Difficulty}
                  </Typography>
                )}
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <Typography className={classes.ques_st} variant2="b1Medium" align="left" sx={{ mb: 2 }}>
                  <MathJaxContent content={questionDescription} />
                </Typography>
                {questionOptions.map((option, index) => {
                  const isSelected = roleState?.getQuesAnsByTrackingIdSuccess?.ans?.selectedOption.includes(option.key);
                  const isCorrect = roleState?.getQuesAnsByTrackingIdSuccess?.ans?.correctOption === option.key;
                  const isUserCorrect =
                    roleState?.getQuesAnsByTrackingIdSuccess?.ans?.correctOption ===
                    roleState?.getQuesAnsByTrackingIdSuccess?.ans?.selectedOption[0];
                  // const isPartiallyCorrect = roleState?.getQuesAnsByTrackingIdSuccess?.ans?.partiallyCorrect === 1;
                  const isMultiSelect = roleState?.getQuesAnsByTrackingIdSuccess?.qus?.Question_Type === "Multi-Select";
                  const showCorrect = !isUserCorrect && isCorrect;

                  return (
                    <AnswerOptionContainer
                      key={index}
                      isSelected={isSelected}
                      isCorrect={isCorrect}
                      isUserCorrect={isUserCorrect}
                      showCorrect={showCorrect}
                      // isPartiallyCorrect={isPartiallyCorrect}
                      isMultiSelect={isMultiSelect}
                    >
                      <MathJaxContent variant2="b2Regular" align="left" content={option.text} />
                    </AnswerOptionContainer>
                  );
                })}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSeeQuestion} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullScreen
        open={openSeeQuestionCoding}
        onClose={handleCloseSeeQuestion}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseSeeQuestion}
          sx={{ position: "absolute", right: 20, top: 20 }}
          size="large"
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ flex: 1, padding: 0 }}>
          <Grid container spacing={2} sx={{ height: "100vh", py: 1, px: 10 }}>
            <Grid item xs={12} md={6} sx={{ height: "100%", overflowY: "auto" }}>
              {!loading && roleState?.codingSubmissionDetailsSuccess ? (
                <Grid
                  sx={{
                    height: "10%",
                    overflow: "auto",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    Question{" "}
                    {Object.keys(roleState.codingSubmissionDetailsSuccess).indexOf(
                      selectedEventState?.questionID.toString()
                    ) + 1}{" "}
                    of {Object.keys(roleState.codingSubmissionDetailsSuccess).length}
                  </Typography>
                  <Button
                    onClick={handlePreviousQuestion}
                    color="primary"
                    sx={{ mx: 5 }}
                    disabled={
                      Object.keys(roleState.codingSubmissionDetailsSuccess).indexOf(
                        selectedEventState?.questionID.toString()
                      ) === 0
                    }
                    variant="outlined"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={handleNextQuestion}
                    color="primary"
                    disabled={
                      Object.keys(roleState.codingSubmissionDetailsSuccess).indexOf(
                        selectedEventState?.questionID.toString()
                      ) ===
                      Object.keys(roleState.codingSubmissionDetailsSuccess).length - 1
                    }
                    variant="outlined"
                  >
                    Next
                  </Button>
                </Grid>
              ) : (
                <Grid
                  sx={{
                    height: "10%",
                    overflow: "auto",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Skeleton variant="rectangular" width="100%" height={"90%"} sx={{ borderRadius: 1 }} />
                </Grid>
              )}
              {loading ? (
                <Grid sx={{ height: "67%", overflow: "auto", borderRadius: 1 }}>
                  <Skeleton variant="rectangular" width="100%" height={"100%"} sx={{ borderRadius: 1 }} />
                </Grid>
              ) : (
                <Grid sx={{ height: "67%", overflow: "auto", border: "1px solid grey", p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ my: 2 }}>
                    Question
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>{questionDescription}</Typography>
                </Grid>
              )}
              {loading ? (
                <Grid
                  sx={{
                    height: "23%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                  }}
                >
                  <Skeleton variant="rectangular" width="100%" height={"90%"} sx={{ borderRadius: 1 }} />
                </Grid>
              ) : (
                <Grid
                  sx={{
                    height: "21.5%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    // justifyContent: "end",
                  }}
                >
                  <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
                    Code Quality Recommendation
                  </Typography>
                  <Typography>
                    {roleState?.codingSubmissionDetailsSuccess?.[selectedEventState?.questionID]
                      ?.codeQualityObservation || ""}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} md={6} sx={{ height: "100%", overflow: "hidden" }}>
              {loading ? (
                <Grid sx={{ height: "10%", overflow: "auto", display: "flex", alignItems: "center" }}>
                  <Skeleton variant="rectangular" width="100%" height={"80%"} sx={{ borderRadius: 1 }} />
                </Grid>
              ) : (
                <Grid sx={{ height: "10%", overflow: "auto", display: "flex", alignItems: "center" }}>
                  <Typography variant="h6">
                    Language: {roleState?.codingSubmissionDetailsSuccess?.[selectedEventState?.questionID]?.language}
                  </Typography>
                </Grid>
              )}
              {loading ? (
                <Grid sx={{ height: "67%", overflow: "auto", borderRadius: 1 }}>
                  <Skeleton variant="rectangular" width="100%" height={"100%"} sx={{ borderRadius: 1 }} />
                </Grid>
              ) : (
                <Grid sx={{ height: "68.5%", overflow: "auto", borderRadius: 1 }}>
                  <MonacoEditor
                    width="100%"
                    height="97%"
                    language={mapLanguage(
                      roleState?.codingSubmissionDetailsSuccess?.[selectedEventState?.questionID]?.language
                    )}
                    theme="vs-dark"
                    value={source}
                    options={{ readOnly: true }}
                  />
                </Grid>
              )}

              {loading ? (
                <Grid
                  sx={{
                    height: "23%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                  }}
                >
                  <Skeleton variant="rectangular" width="100%" height={"90%"} sx={{ borderRadius: 1 }} />
                </Grid>
              ) : (
                <Grid
                  sx={{
                    height: "21.5%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                  }}
                >
                  <Typography variant="h6" sx={{ my: 2 }}>
                    Test Cases
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="test cases table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Input</TableCell>
                          <TableCell>Code Output</TableCell>
                          <TableCell>Expected Output</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testCases.map((testCase, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                maxWidth: "10vw",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tooltip title={testCase.input}>{testCase.input}</Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: "10vw",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tooltip title={testCase.output}>{testCase.output}</Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: "10vw",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tooltip title={testCase.expectedOutput}>{testCase.expectedOutput}</Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: "10vw",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: testCase.result.toLowerCase() === "pass" ? "#4CAF50" : "#F44336",
                                fontWeight: "bold",
                              }}
                            >
                              <Tooltip title={testCase.result}>{testCase.result}</Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimelineSlider;
